<template>
  <div class="bg-grey200 px-16 py-12 sm:py-16 rounded-md">
    <div v-if="currentVariantStockStatus && currentVariantStockStatus.online" class="border-b border-grey400 ">
      <div class="text-xsmall-medium mb-2">{{ sharedResources.stockStatus }}</div>
      <div class="flex justify-between py-8">
        <div class="text-xsmall-medium flex items-center">
          <div
            class="h-8 w-8 rounded-full mr-8 -mt-2"
            :class="{
              'bg-error': !currentVariantStockStatus.online.inStock,
              'bg-success': currentVariantStockStatus.online.inStock,
              'bg-accent': !currentVariantStockStatus.online.inStock && currentVariantStockStatus.online.lowInStock,
            }"
          /> 
          <div>{{ sharedResources.onlineStockStatus }}</div>
        </div>
        <div class="text-xsmall flex">
          <div>
            <span v-if="currentVariantStockStatus.online.inStock">
              {{ productResources.inStock }}
            </span>
            <span v-else-if="!currentVariantStockStatus.online.inStock">
              {{ productResources.temporaryOutOfStockLong }}
            </span>
            <span v-else>{{ productResources.lowInStock }}</span>
          </div>
          <div
            v-if="onlyAvailableInStore"
            class="underline ml-6 hover:no-underline cursor-pointer"
            @click="openGetNotifiedModal"
          >
            {{ productResources.getNotified }}
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="currentVariantStockStatus && currentVariantStockStatus.stores && currentVariantStockStatus.stores.length"
      class="border-b border-grey400 pt-12 pb-4"
    >
      <div class="text-xsmall-medium mb-8">{{ sharedResources.stores }}</div>
      <div
        v-for="store in currentVariantStockStatus.stores"
        :key="store.id"
        class="flex justify-between pb-8"
      >
        <div class="text-xsmall-medium flex items-center">
          <div 
            v-if="!store.disableClickAndCollect"
            class="h-8 w-8 rounded-full mr-8 -mt-2"
            :class="{
              'bg-error': !store.availability.inStock,
              'bg-success': store.availability.inStock,
              'bg-accent': !store.availability.inStock && store.availability.lowInStock,
            }"
          /> 
          <div
            class="underline cursor-pointer hover:no-underline"
            @click="openStoreModal(store.code)"
          >
            {{ store.name }}
          </div>
        </div>
        <div class="text-xsmall">
          <span v-if="store.disableClickAndCollect">
            {{ store.clickAndCollectDisabledMessage }}
          </span>
          <span v-else-if="store.availability.inStock">
            {{ productResources.inStock }}
          </span>
          <span v-else-if="!store.availability.inStock">
            {{ productResources.temporaryOutOfStockLong }}
          </span>
          <span v-else>{{ productResources.lowInStock }}</span>
        </div>
      </div>
    </div>
    <div
      v-if="currentVariantStockStatus"
      class="text-grey800 text-xsmall mt-12"
    >
      {{ sharedResources.updated }}
      <span>{{ getLocaleDate(currentVariantStockStatus.lastUpdated, { year: 'numeric', month: 'numeric', day: 'numeric' }) }}</span>
      <span class="ml-6">{{ getLocaleTime(currentVariantStockStatus.lastUpdated) }}</span>
    </div>
    <transition name="fade-modal">
      <ModalWrapper
        v-if="modalOpen"
        :closeable="true"
        @close="modalOpen = false"
      >
        <div>
          <AllVariantsOutOfStock
            :part-no="productPartNo"
            :is-added-to-favourites="isAddedToFavourites"
            :in-modal="true"
            @add-to-favourites="$emit('add-to-favourites', productPartNo)"
          />
        </div>
      </ModalWrapper>
    </transition>
    <transition name="fade-modal">
      <ModalWrapper
        v-if="storeModalOpen"
        :closeable="true"
        :headline="activeStore.city"
        @close="storeModalOpen = false"
      >
        <div>
          <h3 class="mb-8">{{ activeStore.name }}</h3>
          <div class="store-text mb-16" v-html="activeStore.storeDescription" />
          <StorePageAccordion
            v-if="activeStore"
            :model="activeStore"
          />
          <button class="btn btn--secondary w-full my-24" @click="storeModalOpen = false">
            {{ sharedResources.closeModal }}
          </button>
        </div>
      </ModalWrapper>
    </transition>
  </div>
</template>
  
<script setup lang="ts">
import { useGlobalContentStore } from '~/store/globalContent';
import ModalWrapper from '~/components/modals/ModalWrapper.vue';
import { type IVariantAvailability } from '~/api-types';
import useLocalDate from '~/composables/useLocalDate';
import AllVariantsOutOfStock from '~/components/product-page/AllVariantsOutOfStock.vue';
import { ContentLoader } from '@episerver/content-delivery';
import type { StorePage } from '~/content-types';
import StorePageAccordion from '~/components/store-page/StorePageAccordion.vue';
const globalContentStore = useGlobalContentStore();
const marketSettings = globalContentStore.marketSettings;
const storeListingPage = globalContentStore.pageSettings?.storeListingPage;
const productResources = useGlobalContentStore().productResources;
const sharedResources = useGlobalContentStore().sharedResources;
const { getLocaleDate, getLocaleTime } = useLocalDate();
const modalOpen = ref();
const storeModalOpen = ref();
const activeStore = ref();

defineEmits<{
  (event: 'add-to-favourites', value: string): void,
}>();

const props = defineProps<{
  productAvailability?: IVariantAvailability,
  selectedVariantId: number,
  productPartNo: string,
  isAddedToFavourites: boolean
}>();

const currentVariantStockStatus = computed(() => {
  const variant = props.productAvailability?.find((variant: any) => {
    return variant.variantId === props.selectedVariantId;
  });
  return variant;
});

const onlyAvailableInStore = computed(() => {
  const inStockStore = currentVariantStockStatus.value?.stores.find((store: any) => {
    return store?.availability.inStock;
  });
  return inStockStore && !currentVariantStockStatus.value.online.inStock;
});

const openGetNotifiedModal = () => {
  modalOpen.value = true;
};

const stores = ref();

onMounted(async() => {
  const contentLoader = new ContentLoader();
  await contentLoader.getChildren(storeListingPage.id, { branch: marketSettings.contentLanguage })
    .then((content) => {
      stores.value = content;
    })
    .catch((err) => {
      console.error(err);
    });
});
const openStoreModal = ((code: string) => {
  activeStore.value = stores.value.find((store: StorePage) => {
    return store.warehouseCode === code;
  });
  storeModalOpen.value = true;
});
  
</script>
  
